"use client";

import DotButtons from "@/components/atoms/DotButtons";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import React, { ReactNode, useCallback, useEffect, useState } from "react";

type Props = {
  children: ReactNode[];
  options?: Parameters<typeof useEmblaCarousel>[0];
  className?: string;
  showDots?: boolean;
  slidesPerView?: number;
  slideGap?: string;
  /** Active carousel dot color (maps from CMS `carousel_button_color`). */
  carouselButtonColor?: string;
};

export default function Carousel9({
  children,
  options = {},
  className = "",
  showDots = true,
  slidesPerView = 2.2,
  slideGap = "18px",
  carouselButtonColor,
}: Props) {
  const emblaOptions: Parameters<typeof useEmblaCarousel>[0] = {
    align: "start",
    containScroll: "trimSnaps",
    ...options,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    setSnapCount(emblaApi.scrollSnapList().length);
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (i: number) => emblaApi?.scrollTo(i),
    [emblaApi]
  );

  // Calculate slide width: divide by slidesPerView and account for gaps
  const flexBasis = `calc((100% - (${slideGap} * ${
    slidesPerView - 1
  })) / ${slidesPerView})`;

  return (
    <div className={cn("relative w-full min-w-0", className)}>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex" style={{ gap: slideGap }}>
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              style={{
                flex: `0 0 ${flexBasis}`,
                minWidth: 0,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {showDots && (
        <div className="flex justify-center gap-2 mt-3">
          <DotButtons
            navigationButtonClassName={""}
            clickPrev={() => emblaApi?.scrollPrev()}
            clickNext={() => emblaApi?.scrollNext()}
            selectedIndex={selectedIndex}
            scrollSnaps={Array.from({ length: snapCount })}
            scrollTo={scrollTo}
            selectedDotColor={carouselButtonColor}
          />
        </div>
      )}
    </div>
  );
}
